import {
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Container,
  Box,
  Skeleton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import moment from "moment";

import { FormDatePicker } from "../form/datePicker";
import authServices from "../../services/authServices";
import ModalComponent from "./modalComponent";
import FormField from "../form/formField";
import { saveUser } from "../../redux/reducers/userSlice";

export default function EditProfileModal({ open, handleClose }) {
  const user = useSelector((state) => state.user.data);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [openProgressBar, setOpenProgressBar] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const initialValues = {
    username: user?.username || "",
    mobile: user?.mobileNumber || "",
    gender: user?.gender || "",
    citizenShip: user?.citizenShip || "",
    dob: user?.dob ? moment.unix(user.dob) : null,
    emailId: user?.emailId || "",
    userAddress: {
      flatNo: user?.userAddress?.flatNo || "",
      area: user?.userAddress?.area || "",
      landmark: user?.userAddress?.landmark || "",
      town: user?.userAddress?.town || "",
      state: user?.userAddress?.state || "",
      pincode: user?.userAddress?.pincode || "",
    },
    aadharAddress: {
      flatNo: user?.aadharAddress?.flatNo || "",
      area: user?.aadharAddress?.area || "",
      landmark: user?.aadharAddress?.landmark || "",
      town: user?.aadharAddress?.town || "",
      state: user?.aadharAddress?.state || "",
      pincode: user?.aadharAddress?.pincode || "",
    },
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .trim()
      .required("Username is required")
      .matches(/^[a-zA-Z\s]+$/, "Only letters and spaces are allowed"),
    dob: Yup.date()
      .nullable()
      .required("Date of birth is required")
      .test("age", "Minimum age is 18", (value) => {
        return moment().diff(moment(value), "years") >= 18;
      }),
    userAddress: Yup.object().shape({
      flatNo: Yup.string().trim().nullable().matches(/^[0-9a-zA-Z\s]+$/, "Flat No can only contain numbers and letters"),
      area: Yup.string().trim().required("Area is required"),
      landmark: Yup.string().trim().nullable(),
      town: Yup.string().trim().required("Town/City is required").matches(/^[a-zA-Z\s]+$/, "Only letters are allowed"),
      state: Yup.string().trim().required("State is required").matches(/^[a-zA-Z\s]+$/, "Only letters are allowed"),
      pincode: Yup.string()
        .trim()
        .required("Pincode is required")
        .matches(/^\d{6}$/, "Pincode must be 6 digits"),
    }),
    aadharAddress: Yup.object().shape({
      flatNo: Yup.string().trim().nullable().matches(/^[0-9a-zA-Z\s]+$/, "Flat No can only contain numbers and letters"),
      area: Yup.string().trim().required("Area is required"),
      landmark: Yup.string().trim().nullable(),
      town: Yup.string().trim().required("Town/City is required").matches(/^[a-zA-Z\s]+$/, "Only letters are allowed"),
      state: Yup.string().trim().required("State is required").matches(/^[a-zA-Z\s]+$/, "Only letters are allowed"),
      pincode: Yup.string()
        .trim()
        .required("Pincode is required")
        .matches(/^\d{6}$/, "Pincode must be 6 digits"),
    }),
  });


  const handleSubmit = async (values, { setSubmitting }) => {
    setOpenProgressBar(true);
    try {
      const userDetails = {
        ...values,
        dob: values.dob.unix(),
        _id: user._id,
      };
      const res = await authServices.updatePersonalDetails(userDetails);
      if (res?.success) {
        enqueueSnackbar("Profile updated", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "top" },
        });
        dispatch(saveUser(res.data));
        handleClose(true);
      } else {        
        enqueueSnackbar(res?.data?.message || "Something went wrong", { variant: "error" });
      }
    } catch (err) {  
      enqueueSnackbar(err?.response?.data?.message || "Something went wrong", { variant: "error" });
    } finally {
      setOpenProgressBar(false);
      setSubmitting(false);
    }
  };

  return (
    <ModalComponent open={open} title="Edit Profile" handleClose={handleClose}>
      <Container sx={{ p: 0 }}>
        <Backdrop open={openProgressBar} sx={{ color: "#ddd", zIndex: theme.zIndex.drawer + 1 }}>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: theme.zIndex.modal + 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={75} thickness={4} />
          </Box>
        </Backdrop>

        <Card sx={{ mt: 2 }}>
          <CardContent className="p-0" sx={{ mx: { lg: 5, md: 4, sm: 0, xs: 0 } }}>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, handleChange, setFieldValue, errors, touched, getFieldProps }) => (
                <Form>
                  <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    <Button type="submit" variant="contained" color="primary" size="small">
                      Save
                    </Button>
                    <Button onClick={handleClose} variant="outlined" size="small">
                      Cancel
                    </Button>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ p: 2, boxShadow: 2, borderRadius: 2 }}>
                    <Typography variant="headerMenu">Personal Info</Typography>
                    <Stack direction={isSmallScreen ? "column" : "row"} spacing={2} mt={3}>
                      <FormField
                        name="username"
                        label="User Name"
                        inputProps={{
                          ...getFieldProps("username"),
                          error: Boolean(touched.username && errors.username),
                          helperText: touched.username && errors.username,
                        }}
                      />

                      <FormField
                        label="Legal Name"
                        inputProps={{
                          disabled: true,
                          value: user?.legalName,
                        }}
                      />
                      <FormField
                        label="Mobile No"
                        inputProps={{
                          disabled: true,
                          value: values.mobile,
                        }}
                      />
                    </Stack>

                    <Stack direction={isSmallScreen ? "column" : "row"} spacing={2} mt={3}>
                      <FormField
                        label="Email"
                        inputProps={{
                          disabled: true,
                          value: values.emailId,
                        }}
                      />
                      <FormDatePicker
                        label="Date of Birth"
                        value={values.dob}
                        minAge={18}
                        disabled={true}
                        onChange={(date) => setFieldValue("dob", date)}
                        error={Boolean(touched.dob && errors.dob)}
                        helperText={touched.dob && errors.dob}
                      />
                      <FormField
                        label="Citizenship"
                        inputProps={{
                          disabled: true,
                          value: values.citizenShip,
                        }}
                      />
                    </Stack>
                  </Box>

                  <Box sx={{ p: 2, boxShadow: 2, borderRadius: 2, mt: 4 }}>
                    <Typography variant="headerMenu">Address Info</Typography>

                    <Typography variant="h6" mt={3}>
                      Correspondence Address
                    </Typography>
                    <Stack direction={isSmallScreen ? "column" : "row"} spacing={2} mt={1}>
                      <FormField name="userAddress.flatNo" 
                      label="Flat No, House No"
                       inputProps={{  ...getFieldProps("userAddress.flatNo"),
                        error: Boolean(touched?.userAddress?.flatNo && errors?.userAddress?.flatNo),
                          helperText: touched?.userAddress?.flatNo && errors?.userAddress?.flatNo,
                        }} />
                      <FormField name="userAddress.area" label="Area, Street" 
                      inputProps={{ ...getFieldProps("userAddress.area"),
                        error: Boolean(touched?.userAddress?.area && errors?.userAddress?.area),
                        helperText: touched?.userAddress?.area && errors?.userAddress?.area,
                       }} />
                      <FormField name="userAddress.landmark" label="Landmark" 
                      inputProps={{  ...getFieldProps("userAddress.landmark"),
                        error: Boolean(touched?.userAddress?.landmark && errors?.userAddress?.landmark),
                        helperText: touched?.userAddress?.landmark && errors?.userAddress?.landmark,
                       }} />
                    </Stack>
                    <Stack direction={isSmallScreen ? "column" : "row"} spacing={2} mt={3}>
                      <FormField name="userAddress.town" label="Town/City" 
                      inputProps={{  ...getFieldProps("userAddress.town"),
                        error: Boolean(touched?.userAddress?.town && errors?.userAddress?.town),
                        helperText: touched?.userAddress?.town && errors?.userAddress?.town,
                       }} />
                      <FormField name="userAddress.state" label="State" 
                      inputProps={{  ...getFieldProps("userAddress.state"),
                        error: Boolean(touched?.userAddress?.state && errors?.userAddress?.state),
                        helperText: touched?.userAddress?.state && errors?.userAddress?.state,
                       }} />
                      <FormField name="userAddress.pincode" label="Pincode" 
                      inputProps={{  ...getFieldProps("userAddress.pincode"),
                        error: Boolean(touched?.userAddress?.pincode && errors?.userAddress?.pincode),
                        helperText: touched?.userAddress?.pincode && errors?.userAddress?.pincode,
                       }} />
                    </Stack>

                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h6">Aadhar Address</Typography>

                    <Stack direction={isSmallScreen ? "column" : "row"} spacing={2} mt={1}>
                      <FormField
                        label="Flat No, House No"
                        inputProps={{
                          disabled: Boolean(user?.aadharAddress?.flatNo),
                          value: values.aadharAddress.flatNo,
                          ...getFieldProps("aadharAddress.flatNo"),
                          error: Boolean(touched?.aadharAddress?.flatNo && errors?.aadharAddress?.flatNo),
                          helperText: touched?.aadharAddress?.flatNo && errors?.aadharAddress?.flatNo,
                        }}
                      />
                      <FormField
                        label="Area, Street"
                        inputProps={{
                          disabled: Boolean(user?.aadharAddress?.area),
                          value: values.aadharAddress.area,
                          ...getFieldProps("aadharAddress.area"),
                          error: Boolean(touched?.aadharAddress?.area && errors?.aadharAddress?.area),
                          helperText: touched?.aadharAddress?.area && errors?.aadharAddress?.area,
                        }}
                      />
                      <FormField
                        label="Landmark"
                        inputProps={{
                          disabled: Boolean(user?.aadharAddress?.landmark),
                          value: values.aadharAddress.landmark,
                          ...getFieldProps("aadharAddress.landmark"),
                          error: Boolean(touched?.aadharAddress?.landmark && errors?.aadharAddress?.landmark),
                          helperText: touched?.aadharAddress?.landmark && errors?.aadharAddress?.landmark,
                        }}
                      />
                    </Stack>
                    <Stack direction={isSmallScreen ? "column" : "row"} spacing={2} mt={3}>
                      <FormField
                        label="Town/City"
                        inputProps={{
                          disabled: Boolean(user?.aadharAddress?.town),
                          value: values.aadharAddress.town,
                          ...getFieldProps("aadharAddress.town"),
                          error: Boolean(touched?.aadharAddress?.town && errors?.aadharAddress?.town),
                          helperText: touched?.aadharAddress?.town && errors?.aadharAddress?.town,
                        }}
                      />
                      <FormField
                        label="State"
                        inputProps={{
                          disabled: Boolean(user?.aadharAddress?.state),
                          value: values.aadharAddress.state,
                          ...getFieldProps("aadharAddress.state"),
                          error: Boolean(touched?.aadharAddress?.state && errors?.aadharAddress?.state),
                          helperText: touched?.aadharAddress?.state && errors?.aadharAddress?.state,
                        }}
                      />
                      <FormField
                        label="Pincode"
                        inputProps={{
                          disabled: Boolean(user?.aadharAddress?.pincode),
                          value: values.aadharAddress.pincode,
                          ...getFieldProps("aadharAddress.pincode"),
                          error: Boolean(touched?.aadharAddress?.pincode && errors?.aadharAddress?.pincode),
                          helperText: touched?.aadharAddress?.pincode && errors?.aadharAddress?.pincode,
                        }}
                      />
                    </Stack>
                  </Box>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Container>
    </ModalComponent>
  );
}
